import { useReactiveVar } from "@apollo/client";
import { Button, Heading } from "@dmm-com/rocket-web-ui-library";
import type { SelectOption } from "@dmm-com/rocket-web-ui-library/es6/components/select";
import { memo, useEffect, useMemo, useState } from "react";
import { convertByteToGigaByte } from "./convert-byte-to-gigabyte";
import * as Styled from "./style";
import { PortalModal } from "~/components/blocks/common/portal-modal";
import {
  isOpenDownloadModalVar,
  downloadModalDataVar,
  clickedContentIdVar,
} from "~/components/stores/detail-product/store";
import { openTextOnlyToast } from "~/components/stores/toast/store";
import { useHandleClickByDevice } from "~/hooks/use-handle-click-by-device";
import { useQueryString } from "~/hooks/use-query-string";
import { G_VideoContentType, G_VideoSeasonType, G_DownloadType, useFetchDownloadFileUrlLazyQuery } from "~/lib/graphql";
import { Z_INDEX } from "~/styles/z-index";

const DMM_2D_MEDIA_VIEW_METHOD_URL = "https://support.dmm.com/akb48/article/47705";
const DMM_VR_MEDIA_VIEW_METHOD_URL = "https://support.dmm.com/dmm-tv/article/47593";

/**
 * ダウンロードボタンを押したときに開くダウンロードモーダル
 */
export const DownloadPortalModal = memo(() => {
  // MEMO（kimura）: 前提条件 : ダウンロードURLは期限付きURLである
  // MEMO（kimura）: なので、キャッシュされてしまうと、最悪有効期限が切れてしまうため、fetchPolicyを変更する（矢野さんに確認済み）
  const [downloadFileUrlExec] = useFetchDownloadFileUrlLazyQuery({ fetchPolicy: "network-only" });

  const isDownloadModalOpen = useReactiveVar(isOpenDownloadModalVar);
  const clickedContentId = useReactiveVar(clickedContentIdVar);
  const { titleName, seasonType, contentType, downloadableFiles } = useReactiveVar(downloadModalDataVar) || {};
  const { season } = useQueryString();
  const { handleClickByDevice } = useHandleClickByDevice();

  const [selectedValue, setSelectedValue] = useState("");
  // MEMO（kimura）: ダウンロードURLを生成中の`loadingフラグ`は`useState`で管理する
  const [pressedPartNumber, setPressedNumber] = useState(0);

  const selectOptions: SelectOption[] = useMemo(() => {
    if (downloadableFiles === undefined || downloadableFiles === null || downloadableFiles.length === 0) return [];

    // MEMO（kimura）: TypeError: "x" is read-only を防ぐために配列をコピーした後に sort関数 を実行している
    return [...downloadableFiles]
      .sort((a, b) => a.quality.displayPriority - b.quality.displayPriority)
      .map(({ quality: { name, displayName } }) => {
        return {
          listValue: name,
          optionLabelElement: displayName,
        };
      });
  }, [downloadableFiles]);

  const downloadBtnArray = useMemo(() => {
    if (downloadableFiles === undefined || downloadableFiles === null || downloadableFiles.length === 0) return [];

    // MEMO（kimura）: TypeError: "x" is read-only を防ぐために配列をコピーした後に sort関数 を実行している
    return [...downloadableFiles]
      .sort((a, b) => a.quality.displayPriority - b.quality.displayPriority)
      .flatMap(({ quality: { name }, parts }) => {
        return parts.map(({ fileSize, partNumber }) => {
          return {
            name,
            fileSize,
            partNumber,
          };
        });
      });
  }, [downloadableFiles]);

  const [downloadModalData, setDownloadModalData] = useState(downloadBtnArray);

  const handleOptionClick = (selectedValue: string) => {
    setSelectedValue(selectedValue);
    setDownloadModalData(downloadBtnArray.filter((item) => item.name === selectedValue));
  };

  const createDownloadFileUrl = async (partNumber: number) => {
    if (season === undefined) return;

    try {
      setPressedNumber(partNumber);

      const { data: downloadFileUrl } = await downloadFileUrlExec({
        variables: {
          id: clickedContentId,
          qualityName: selectedValue,
          /** VRコンテンツの場合 : WSDCF | 2Dコンテンツの場合 : DCV */
          fileType: contentType === G_VideoContentType.VodVr ? G_DownloadType.Wsdcf : G_DownloadType.Dcv,
          /** partsの配列の合計数が1より大きい場合は`partNumber` それ以外の場合は`0`を指定する */
          part: downloadModalData.length > 1 ? partNumber : 0,
        },
      });

      if (!downloadFileUrl || !downloadFileUrl.downloadFileUrl) {
        setPressedNumber(0);
        return;
      }

      const downLoadLink = document.createElement("a");
      downLoadLink.href = downloadFileUrl.downloadFileUrl;
      downLoadLink.download = downloadFileUrl.downloadFileUrl;
      document.body.appendChild(downLoadLink);
      downLoadLink.click();
      document.body.removeChild(downLoadLink);

      openTextOnlyToast("START_DOWNLOAD");
      setPressedNumber(0);
      isOpenDownloadModalVar(false);
    } catch {
      setPressedNumber(0);
      openTextOnlyToast("FAILED_DOWNLOAD");
    }
  };

  useEffect(() => {
    if (selectOptions.length === 0) return;
    setSelectedValue(selectOptions[0]["listValue"]);
    const filteredArray = downloadBtnArray.filter((item) => item.name === selectOptions[0]["listValue"]);
    setDownloadModalData(filteredArray);
  }, [downloadableFiles]);

  return (
    <PortalModal
      size="middle"
      basisZIndex={Z_INDEX.MODAL_IN_MODAL}
      isOpen={isDownloadModalOpen}
      isShowBodyCloseButton
      onCloseModal={() => isOpenDownloadModalVar(false)}
    >
      <Styled.DivRootGrid>
        <Styled.HeadingSeasonTitle>{titleName}</Styled.HeadingSeasonTitle>

        <Styled.SecFlexContainer>
          <Heading size="M">ダウンロード</Heading>

          <Styled.Select
            minWidth="320px"
            selectedValue={selectedValue}
            options={selectOptions}
            onOptionClick={handleOptionClick}
          />
        </Styled.SecFlexContainer>

        {downloadModalData.length > 0 && (
          <Styled.DivButtonFlexContainer partLength={downloadModalData.length}>
            {downloadModalData
              .filter((item) => item.name === selectedValue)
              .map(({ partNumber, fileSize }, idx, array) => {
                // MEMO（kimura）: ファイルの種類が1種類の場合 : ダウンロードを表示する
                if (array.length === 1) {
                  return (
                    <Button
                      key={`download-button-${idx}`}
                      type="button"
                      name="Download Button"
                      iconName={pressedPartNumber === partNumber ? "loading" : "download"}
                      iconColor="GRAY50"
                      color="GRAY50"
                      bgColor="GRAY1000"
                      width="100%"
                      mainText={pressedPartNumber === partNumber ? "ダウンロード中..." : `ダウンロード`}
                      subText={`（${convertByteToGigaByte(fileSize)}GB）`}
                      {...handleClickByDevice(() => createDownloadFileUrl(partNumber))}
                    />
                  );
                }

                // MEMO（kimura）: パートに分かれている場合 : パート番号を横並びで表示する
                return (
                  <Button
                    key={`download-button-${idx}`}
                    type="button"
                    name="Download Button"
                    iconName={pressedPartNumber === partNumber ? "loading" : "download"}
                    iconColor="GRAY50"
                    color="GRAY50"
                    bgColor="GRAY1000"
                    width="100%"
                    mainText={pressedPartNumber === partNumber ? "ダウンロード中..." : `パート${partNumber}`}
                    subText={`（${convertByteToGigaByte(fileSize)}GB）`}
                    {...handleClickByDevice(() => createDownloadFileUrl(partNumber))}
                  />
                );
              })}
          </Styled.DivButtonFlexContainer>
        )}

        <Styled.DivDownloadGridContainer>
          {(() => {
            if (seasonType === undefined) return null;

            if (seasonType === G_VideoSeasonType.Legacy) {
              if (contentType === G_VideoContentType.Vod_2D) {
                return (
                  <>
                    <Styled.ParText>ダウンロードした動画をパソコンで視聴するには</Styled.ParText>

                    <Styled.SpanText>
                      専用プレイヤー「
                      <Styled.TextLink href={DMM_2D_MEDIA_VIEW_METHOD_URL} color="BLUE900">
                        DMM Player v2
                      </Styled.TextLink>
                      」が必要です
                    </Styled.SpanText>
                  </>
                );
              }

              if (contentType === G_VideoContentType.VodVr) {
                return (
                  <>
                    <Styled.ParText>VR動画の視聴には専用プレイヤーが必要です</Styled.ParText>
                    <Styled.TextLink href={DMM_VR_MEDIA_VIEW_METHOD_URL} color="BLUE900">
                      ・VR動画の視聴方法について
                    </Styled.TextLink>
                  </>
                );
              }
            }

            if (seasonType === G_VideoSeasonType.Stage) {
              if (contentType === G_VideoContentType.Live_2D) {
                return (
                  <>
                    <Styled.ParText>ダウンロードした動画をパソコンで視聴するには</Styled.ParText>

                    <Styled.SpanText>
                      専用プレイヤー「
                      <Styled.TextLink href={DMM_2D_MEDIA_VIEW_METHOD_URL} color="BLUE900">
                        DMM Player v2
                      </Styled.TextLink>
                      」が必要です
                    </Styled.SpanText>
                  </>
                );
              }

              if (contentType === G_VideoContentType.LiveVr) {
                return (
                  <>
                    <Styled.ParText>VR動画の視聴には専用プレイヤーが必要です</Styled.ParText>
                    <Styled.TextLink href={DMM_VR_MEDIA_VIEW_METHOD_URL} color="BLUE900">
                      ・VR動画の視聴方法について
                    </Styled.TextLink>
                  </>
                );
              }
            }

            return (
              <>
                <Styled.ParText>ダウンロードした動画の再生には専用のプレイヤーが必要です。</Styled.ParText>
                <Styled.TextLink href={DMM_2D_MEDIA_VIEW_METHOD_URL} color="BLUE900">
                  ・動画の視聴方法
                </Styled.TextLink>
              </>
            );
          })()}
        </Styled.DivDownloadGridContainer>
      </Styled.DivRootGrid>
    </PortalModal>
  );
});

DownloadPortalModal.displayName = "DownloadPortalModal";
